<script lang="ts">
  /**
   * ScoresDrawer — what the top-right ring opens (Scott, 2026-09-10: "make the
   * goal section a drawer that comes up to set and show your goal").
   *
   * Top to bottom: this week vs the goal with the goal editable inline, weeks
   * in a row (HistoryRings); the last 8 weeks (DistanceTrendCard); the Your
   * scores tiles (ScoresTiles; lane default, since Scott named the ring "the
   * scores"). All three moved here from the Sets tab unchanged.
   *
   * Goal editing lives here, never in a second sheet on top of this one. No
   * goal set: the drawer opens on "Set a weekly goal". A save updates the
   * shared goal store at once, so the top-bar ring moves before this closes;
   * the durable write is journaled (commitGoalChange) exactly as the old goal
   * sheet did. Every distance here is in meters (persona gate B1).
   */
  import { untrack } from "svelte";
  import type { GoalPreferences } from "@contract/types";
  import { bridge } from "../bridge/client";
  import HistoryRings from "./HistoryRings.svelte";
  import DistanceTrendCard from "./DistanceTrendCard.svelte";
  import ScoresTiles from "./ScoresTiles.svelte";
  import { workouts } from "../lib/workoutsStore";
  import { aggregateRecentWeeks } from "../lib/aggregates";
  import { closeOverlay, goalPrefs, setGoalPrefs } from "../lib/overlay";
  import { commitGoalChange } from "../lib/goalCorrection";
  import { unsetGoals } from "../lib/goalDefaults";
  import { haptic } from "../lib/haptics";
  import { fmtMeters } from "../lib/format";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";

  let { open = false }: { open?: boolean } = $props();

  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  });

  let goalM = $derived($goalPrefs?.weekDistanceMeters ?? null);
  // Save needs the full saved goals to merge into (peak HR and age ride along):
  // on a shell, wait for them; a browser has nothing to read.
  let canSave = $derived($goalPrefs !== null || !bridge.isAvailable());

  // Median of the last 4 COMPLETED weeks, in meters: the same suggestion the
  // old goal sheet gave (MetricRings' rule), rounded to a 50 m step.
  let typicalWeekM = $derived.by((): number | null => {
    const prior = aggregateRecentWeeks($workouts, 5).slice(0, 4);
    if (prior.every((w) => w.totalDistanceMeters === 0)) return null;
    const sorted = prior.map((w) => w.totalDistanceMeters).sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    const med = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    return Math.round(med / 50) * 50;
  });

  let editing = $state(false);
  let goalInput = $state(3000);

  // Each time the drawer opens: no goal -> straight into "Set a weekly goal";
  // a goal -> show it, with Change.
  $effect(() => {
    if (!open) return;
    untrack(() => {
      editing = goalM === null;
      goalInput = goalM ?? typicalWeekM ?? 3000;
    });
  });

  function startEdit() {
    haptic("selection");
    goalInput = goalM ?? typicalWeekM ?? 3000;
    editing = true;
  }

  function cancelEdit() {
    editing = false;
  }

  function saveGoal() {
    const meters = Math.max(0, Math.round(goalInput / 50) * 50);
    const next: GoalPreferences = {
      ...($goalPrefs ?? unsetGoals()),
      weekDistanceMeters: meters > 0 ? meters : null,
    };
    commitGoalChange(next);
    setGoalPrefs(next);
    haptic("success");
    editing = false;
  }

  function close() {
    closeOverlay("scores");
  }

  function onKey(e: KeyboardEvent) {
    if (open && e.key === "Escape") close();
  }
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div class="scrim" role="presentation" onclick={close}></div>
  <div class="sheet" role="dialog" aria-modal="true" aria-label="Scores" use:sheetDrag={close}>
    <div class="grabber" aria-hidden="true"></div>
    <div class="head">
      <h2 class="title">Scores</h2>
      <button type="button" class="done" onclick={close}>Done</button>
    </div>

    <div class="stack">
      <HistoryRings workouts={$workouts} onEditGoal={startEdit} />

      {#if editing}
        <form
          class="goal-form"
          aria-label="Set a weekly goal"
          onsubmit={(e) => {
            e.preventDefault();
            saveGoal();
          }}
        >
          <p class="form-title">{goalM === null ? "Set a weekly goal" : "Change your weekly goal"}</p>
          <div class="num-row">
            <input
              type="number"
              min="0"
              max="50000"
              step="50"
              inputmode="numeric"
              bind:value={goalInput}
              aria-label="Weekly goal in meters"
            />
            <span class="unit">m a week</span>
          </div>
          <input
            type="range"
            min="0"
            max="20000"
            step="250"
            bind:value={goalInput}
            aria-label="Weekly goal slider"
          />
          <p class="hint">
            {typicalWeekM !== null
              ? `Your typical week lately: ${fmtMeters(typicalWeekM)}`
              : "Not enough swims yet to suggest a goal."}
          </p>
          <div class="actions">
            {#if goalM !== null}
              <button type="button" class="btn ghost" onclick={cancelEdit}>Cancel</button>
            {/if}
            <button type="submit" class="btn primary" disabled={!canSave}>Save goal</button>
          </div>
        </form>
      {:else}
        <div class="goal-row">
          <span class="goal-text">Weekly goal <b>{fmtMeters(goalM ?? 0)}</b></span>
          <button type="button" class="change" onclick={startEdit}>Change</button>
        </div>
      {/if}

      <DistanceTrendCard workouts={$workouts} />

      <ScoresTiles workouts={$workouts} />
    </div>
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    touch-action: none;
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 51;
    /* Opaque, like the goal sheet it replaces: the global --bg-card is
       translucent and a dialog gets no blur, so the page ghosted through. */
    --bg-card: var(--gray-1);
    background: var(--bg-card);
    border-top: 1px solid var(--divider);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    padding: var(--space-3) var(--space-4)
      calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
    box-shadow: var(--shadow-floating);
    animation: sheet-in var(--dur-card) var(--ease-swim);
    max-height: 90vh;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .grabber {
    width: 36px;
    height: 4px;
    border-radius: 999px;
    background: var(--divider-strong);
    margin: 0 auto var(--space-2);
  }
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-2);
  }
  .title {
    font: var(--type-title-subtitle);
    color: var(--text-primary);
    margin: 0;
  }
  .done {
    min-height: 44px;
    padding: 0 var(--space-2);
    margin-right: calc(-1 * var(--space-2));
    border: 0;
    background: transparent;
    color: var(--accent-primary);
    font: inherit;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .stack {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  /* Goal, shown */
  .goal-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    margin-top: calc(-1 * var(--space-2));
    padding: 0 2px;
  }
  .goal-text {
    font-size: 15px;
    color: var(--text-muted);
  }
  .goal-text b {
    color: var(--text-primary);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .change {
    min-height: 44px;
    padding: 0 var(--space-2);
    margin-right: calc(-1 * var(--space-2));
    border: 0;
    background: transparent;
    color: var(--accent-primary);
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
  }

  /* Goal, editing (the old goal sheet's form, in meters) */
  .goal-form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    background: var(--bg-surface);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: var(--space-4);
  }
  .form-title {
    margin: 0;
    font: var(--type-title-subtitle);
    color: var(--text-primary);
  }
  .num-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .num-row input[type="number"] {
    flex: 0 0 120px;
    padding: var(--space-2) var(--space-3);
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-button);
    color: var(--text-primary);
    font: var(--type-title-subtitle);
    font-variant-numeric: tabular-nums;
  }
  .num-row input[type="number"]:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 1px;
  }
  .unit {
    font: var(--type-body-sm);
    color: var(--text-muted);
  }
  input[type="range"] {
    width: 100%;
    accent-color: var(--accent-primary);
  }
  .hint {
    margin: 0;
    font: var(--type-body-sm);
    color: var(--text-muted);
  }
  .actions {
    display: flex;
    gap: var(--space-2);
    justify-content: flex-end;
  }
  .btn {
    min-height: 44px;
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-button);
    border: 1px solid var(--divider);
    background: var(--bg-raised);
    color: var(--text-primary);
    font: var(--type-title-subtitle);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .btn.primary {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: var(--text-on-accent);
    font-weight: 700;
  }
  .btn.ghost {
    background: transparent;
  }
  .btn:disabled {
    background: var(--bg-raised);
    border-color: var(--divider);
    color: var(--text-muted);
    cursor: not-allowed;
  }

  @keyframes scrim-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes sheet-in {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
</style>
